import React, { useEffect, useState } from 'react';
import { Autocomplete, Button, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import PaymentByUserScreen from './PaymentsToolGrids/user/PaymentByUserScreen';
import PaymentsTableByYearScreen from './PaymentsToolGrids/year/PaymentsByYearScreen';
import PaymentsByPeriodsScreen from './PaymentsToolGrids/period/PaymentsByPeriodsScreen';
import { useDispatch } from 'react-redux';
import { apiSlice } from '../../../app/api/apiSlice';
import { useGetAccountsFastListQuery } from '../../../services/accountsApi';
import { useLocation, useHistory } from "react-router-dom";
import { updateQueryParam } from '../../../utils';
import PaymentInformation from './PaymentsToolGrids/PaymentInformation/PaymentInformation';
import { useGetPeriodsQuery } from '../../../services/PaymentToolApi';

function getYearsFrom() {
  let years = [];
  let currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2024; i--) {
    years.push(i);
  }
  return years;
}

// period item example in periodsArray
// {
//   "id": 3,
//   "year": 2023,
//   "startDate": "2023-01-16T00:00:00",
//   "endDate": "2023-01-22T23:59:59.997",
//   "isCompleted": false
// }
function getPeriods(periodsArray, selectedYear) {
  //filter periods by year
  
  periodsArray = periodsArray.filter(period => period.year == selectedYear);

  //filter periods that are in the future, must include the current day
  let currentDate = new Date();
  periodsArray = periodsArray.filter(period => new Date(period.startDate) <= currentDate);

  //sort periods by date, from newest to oldest
  periodsArray.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  
  return periodsArray.map(period => ({
    start: period.startDate.split('T')[0],
    end: period.endDate.split('T')[0]
  }));
}

function getOptionValue(start, end) {
  function reformatDate(dateStr) {
    let parts = dateStr.split("-");
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  let startFormatted = reformatDate(start);
  let endFormatted = reformatDate(end);
  return `${startFormatted}-${endFormatted}`;
}

function periodsToOptions(periodsFromApi, selectedYear) {
  let periods = getPeriods(periodsFromApi, selectedYear);
  return periods.map((period, index) => (
    <MenuItem key={index} value={getOptionValue(period.start, period.end)}>
      {getOptionValue(period.start, period.end)}
    </MenuItem>
  ));
}

function getDefaultsValues(searchParams) {
  return {
    period: searchParams.get("period") || "",
  }
}

const PayoutManagement = () => {
  const {data: periods, isLoading: isLoadingPeriods, isFetching: isFetchingPeriods, error: errorPeriods} = useGetPeriodsQuery();
  const dispatch = useDispatch();
  let years = getYearsFrom();
  const [selectedYear, setSelectedYear] = useState([years.find(year => year === new Date().getFullYear()) || 2021]);
  const [periodsForYear, setPeriodsForYear] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const history = useHistory();
  const showPeriodPaymentInformation = query.get('showPeriodPaymentInformation');
  const {period: defaultPeriod} = getDefaultsValues(query)
  const [selectedPeriod, setSelectPeriod] = useState(defaultPeriod);

  const { data: usersRaw, isLoading: isLoadingUsers, isFetching: isFetchingUsers, error: errorUsers } = useGetAccountsFastListQuery({ isDeleted: false, userType: ['NewBee', 'Bee'] });

  let users = usersRaw?.map(user => ({ id: user.id, fullName: user.fullName }));

  if(users && users.length > 0 && !selectedUser) {
    let userIdQuery = query.get("user");
    if(userIdQuery && userIdQuery.length > 0) {
      let userFound = users.find(user => user.id == userIdQuery);
      if(userFound) {
        setSelectedUser(users.find(user => user.id == userIdQuery));
      }
    }
  }

  const invalidateCache = () => {
    if(selectedUser) dispatch(apiSlice.util.invalidateTags(['userMetrics', 'transactionSummary', 'period']))
    else if (selectedYear && selectedPeriod) {
      dispatch(apiSlice.util.invalidateTags(['periodMetrics', 'transactionSummary', 'period']));
    } else {
      dispatch(apiSlice.util.invalidateTags(['yearMetrics', 'transactionSummary']));
    }
  };

function onUserChange(e, value) {
    if (!value) {
      updateQueryParam(history, location, "user", "");
      setSelectedUser(null);
    } else {
      updateQueryParam(history, location, "user", value.id);
      setSelectedUser(value);
    }
}

  function onChangeYear(e) {
    if (e.target.value == "") return setSelectedYear("");
    let year = parseInt(e.target.value);
    
    setSelectedYear(year);
    setPeriodsForYear(periodsToOptions(periods || [], year));
    setSelectPeriod(null);
  }

  function setSelectPeriodHandler(period){
    setSelectPeriod(period);
  }

  useEffect(() => {
    setPeriodsForYear( periodsToOptions(periods || [], selectedYear) );
  }
  , [periods]);
  
  //A: when a period is closed and the sync payment against Paypal is successful the user can click on the "View Payment Information" button to see the payment information.
  //A: this component is used to display the payment information for a period.
  //so under url path: /dashboard/admintools?tab_index=4&showPeriodPaymentInformation={periodId} the user can see the payment information for the period with the id {periodId}
  //and under url path: /dashboard/admintools?tab_index=4 the user can see the payout management screen.
  if (showPeriodPaymentInformation) {
    return <PaymentInformation periodID={showPeriodPaymentInformation} />;
  }

  return (
    <div style={{ margin: '0', padding: '0' }} >
      {/* Title */}
      <h3 style={{ margin: "0 0 0.5em 0" }} >
        Honeypot Manager
      </h3>
      {/* Select components for year, period and user */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', flexWrap: 'wrap' }} >
        <FormControl style={{ margin: '0 0 0.5em 0' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <p style={{ fontSize: 'var(--p2)', margin: '0 0.25em 0 0' }}>Year:</p>
            <Select
              sx={{ height: '36px', width: '100px', margin: '0 1em 0 0', borderRadius: '8px' }}
              onChange={onChangeYear}
              value={selectedYear}
            >
              {years.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
            </Select>
          </div>
        </FormControl>

        <FormControl style={{ margin: '0 0 0.5em 0' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <p style={{ fontSize: 'var(--p2)', margin: '0 0.25em 0 0', whiteSpace: 'nowrap' }}>Select Remittance Period:</p>
            <Select
              sx={{ height: '36px', width: '200px', margin: '0 1em 0 0', borderRadius: '8px' }}
              onChange={(e) => setSelectPeriod(e.target.value)}
              value={selectedPeriod}
            >
              {
                !selectedUser &&
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
              }
              {periodsForYear}
            </Select>
          </div>
        </FormControl>
        
        <FormControl style={{ margin: '0 0 0.5em 0' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} >
            <p style={{ fontSize: 'var(--p2)', margin: '0 0.25em 0 0', whiteSpace: 'nowrap' }} >Bee User:</p>
            <Autocomplete
              disablePortal
              onChange={onUserChange}
              value={selectedUser}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              id="combo-box-demo"
              options={(users || [])}
              sx={{width: '200px', margin: '0 1em 0 0', borderRadius: '8px', height: '36px'}}
              getOptionLabel={(option) => option.fullName}
              renderInput={(params) => <TextField {...params} sx={{
                "& .MuiInputBase-root": { height: "36px",  borderRadius: '8px' },
                "& .MuiInputBase-input": { borderRadius: '8px',margin: '0', padding: '0'},
                "& .MuiOutlinedInput-root": { padding: '0 0 0 0.2em'},
              }} />}
              disabled={!selectedPeriod || !selectedYear}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.fullName}
                  </li>
                );
              }}
            />
          </div>
        </FormControl>

        <Button variant="outlined" sx={{ margin: '0 0 0.5em 0', width: '100px' }} onClick={() => invalidateCache()}>
          Update
        </Button>



      </div>

      {
        !selectedPeriod && !selectedUser && <PaymentsTableByYearScreen years={years} selectedYear={selectedYear} setSelectedYear={setSelectedYear} setSelectPeriod={setSelectPeriodHandler} />
      }

      {
        selectedPeriod && !selectedUser && <PaymentsByPeriodsScreen selectedYear={selectedYear} selectedPeriod={selectedPeriod} setSelectedUser={setSelectedUser}/>
      }
      {
        selectedUser && <PaymentByUserScreen selectedYear={selectedYear} selectedPeriod={selectedPeriod} selectedUser={selectedUser} />
      }

    </div>
  );
}

export default PayoutManagement;
